/**
 * The console's one way to write money, counts, token totals, shares and dates (plan step 2, "one lib/format.ts").
 * Before this, the same dollar figure read differently on three screens (four decimals, whole dollars over $10, cents
 * dropped over $10) and token totals had three formats. Every new screen formats through here; the legacy screens move
 * over as they are rebuilt.
 *
 * Pure and locale-fixed (en-US), so render tests and screenshots are stable. Dates use the viewer's time zone unless a
 * `timeZone` is given. A value that is missing or not a number reads as an em dash, never "NaN" or "$undefined".
 */

const DASH = '—';
const LOCALE = 'en-US';

const isNum = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

const usd2 = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const usd4 = new Intl.NumberFormat(LOCALE, { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 });
const whole = new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 });

/**
 * US dollars, always with cents: `$1,284.36`, `$0.00`, `-$3.10`. A non-zero amount under one cent keeps four decimals
 * (`$0.0042`) so a small cost never reads as free.
 */
export function money(n: number | null | undefined): string {
  if (!isNum(n)) return DASH;
  const abs = Math.abs(n);
  if (abs > 0 && abs < 0.01) return usd4.format(n);
  return usd2.format(n);
}

/** A whole count with grouping: `18,452`. Fractions are rounded. */
export function count(n: number | null | undefined): string {
  if (!isNum(n)) return DASH;
  return whole.format(Math.round(n));
}

/**
 * A large count in short form, one decimal: `96.4M`, `12.0K`, `1.2B`; under a thousand it is the plain count (`438`).
 * Used for token totals, where the exact figure is noise.
 */
export function compact(n: number | null | undefined): string {
  if (!isNum(n)) return DASH;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}K`;
  return count(n);
}

/**
 * A token total: millions from a tenth of a million up (`0.8M`, `31.4M`), so a column of token counts reads in one unit;
 * below that, `compact` (`12.0K`, `438`).
 */
export function tokens(n: number | null | undefined): string {
  if (!isNum(n)) return DASH;
  const abs = Math.abs(n);
  if (abs >= 1e5 && abs < 1e9) return `${n < 0 ? '-' : ''}${(abs / 1e6).toFixed(1)}M`;
  return compact(n);
}

/** `part / total` as a ratio from 0 to 1; 0 when the total is zero or missing (never NaN or Infinity). */
export function ratio(part: number | null | undefined, total: number | null | undefined): number {
  if (!isNum(part) || !isNum(total) || total <= 0) return 0;
  return part / total;
}

/** A ratio (0 to 1) as a percentage: `percent(0.437, 1)` is `43.7%`, `percent(0.31)` is `31%`. */
export function percent(value: number | null | undefined, digits = 0): string {
  if (!isNum(value)) return DASH;
  return `${(value * 100).toFixed(digits)}%`;
}

/** `1 error`, `312 errors`; the count is grouped. */
export function plural(n: number, word: string, many = `${word}s`): string {
  return `${count(n)} ${n === 1 ? word : many}`;
}

type DateInput = string | number | Date | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

interface ZoneOption {
  /** An IANA zone such as 'UTC' or 'America/Los_Angeles'; the viewer's own zone when absent. */
  timeZone?: string;
}

/** `Oct 1, 2026`. */
export function date(value: DateInput, opts: ZoneOption = {}): string {
  const d = toDate(value);
  if (!d) return DASH;
  return new Intl.DateTimeFormat(LOCALE, { month: 'short', day: 'numeric', year: 'numeric', timeZone: opts.timeZone }).format(d);
}

/** `08:52`, 24-hour. With `zone: true`, the zone's short name follows: `17:00 PDT`. */
export function time(value: DateInput, opts: ZoneOption & { zone?: boolean } = {}): string {
  const d = toDate(value);
  if (!d) return DASH;
  const parts = new Intl.DateTimeFormat(LOCALE, {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: opts.timeZone, ...(opts.zone ? { timeZoneName: 'short' as const } : {}),
  }).formatToParts(d);
  const hour = parts.find((p) => p.type === 'hour')?.value ?? '';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '';
  const zone = opts.zone ? parts.find((p) => p.type === 'timeZoneName')?.value : undefined;
  return zone ? `${hour}:${minute} ${zone}` : `${hour}:${minute}`;
}

/** `Oct 1, 2026, 08:52`. */
export function dateTime(value: DateInput, opts: ZoneOption = {}): string {
  const d = toDate(value);
  if (!d) return DASH;
  return `${date(d, opts)}, ${time(d, opts)}`;
}

/** The next midnight UTC after `value`: when a "per UTC day" cap starts over. */
export function nextUtcMidnight(value: DateInput): Date | null {
  const d = toDate(value);
  if (!d) return null;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1));
}
